import { useState } from "react";
import { useParams } from "react-router-dom";

import { Tabs } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterProceduresProvider } from "@/encounters/contexts/EncounterProceduresContext";
import { EncounterAnswersFormType } from "@/encounters/zod-schemas/encounter-answers";
import { NewbornIPPEncounterFormType } from "@/encounters/zod-schemas/newborn-ipp-encounter";

import classes from "../EncounterTabs/EncounterTabs.module.css";

import DxPlanTabPanel from "./DxPlanTabPanel";

enum DxClinicalTabsEnum {
  OBJECTIVE_CLINICAL_NOTES = "objective-clinical-notes",
  DX_ORDERS_PLAN = "dx-orders-plan",
}

interface DxClinicalTabsProps {
  form: UseFormReturnType<EncounterAnswersFormType | NewbornIPPEncounterFormType>;
  children: React.ReactNode;
  isEncounterActive: boolean;
  isAmendment?: boolean;
}

// FIXME: Rename this to something like EncounterFormTabs
export default function DxClinicalTabs({
  form,
  children,
  isEncounterActive,
  isAmendment,
}: DxClinicalTabsProps) {
  const { clientId, encounterId } = useParams<{ clientId: string; encounterId: string }>();
  const [activeTab, setActiveTab] = useState<DxClinicalTabsEnum>(
    DxClinicalTabsEnum.OBJECTIVE_CLINICAL_NOTES,
  );

  const handleTabVariant = (tab: DxClinicalTabsEnum) => {
    return activeTab === tab ? "bold" : "default";
  };

  return (
    // The Objective panel's Procedures section and the Dx tab's Diagnoses section both read the
    // encounter's clinical Procedures and Conditions and stage onto the same save, so the read is
    // owned here rather than by either panel.
    <EncounterProceduresProvider clientId={clientId} encounterId={encounterId}>
      <Tabs
        radius="md"
        color="gray"
        variant="pills"
        value={activeTab}
        data-testid="encounter-tabs"
        onChange={(value) => setActiveTab(value as DxClinicalTabsEnum)}
      >
        <Tabs.List className={classes.tabsList}>
          <Tabs.Tab
            className={classes.tab}
            data-testid="encounter-tab-objective"
            value={DxClinicalTabsEnum.OBJECTIVE_CLINICAL_NOTES}
          >
            <TextWrapper variant={handleTabVariant(DxClinicalTabsEnum.OBJECTIVE_CLINICAL_NOTES)}>
              Objective Clinical Data
            </TextWrapper>
          </Tabs.Tab>
          <Tabs.Tab
            className={classes.tab}
            value={DxClinicalTabsEnum.DX_ORDERS_PLAN}
            data-testid="encounter-tab-dx-orders-plan"
          >
            <TextWrapper variant={handleTabVariant(DxClinicalTabsEnum.DX_ORDERS_PLAN)}>
              Dx, Orders & Plan
            </TextWrapper>
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={DxClinicalTabsEnum.OBJECTIVE_CLINICAL_NOTES}>{children}</Tabs.Panel>

        <Tabs.Panel value={DxClinicalTabsEnum.DX_ORDERS_PLAN}>
          <DxPlanTabPanel
            form={form}
            isAmendment={isAmendment}
            isEncounterActive={isEncounterActive}
          />
        </Tabs.Panel>
      </Tabs>
    </EncounterProceduresProvider>
  );
}
